import React, { useState } from "react";
import { Box, Checkbox, FormControlLabel, Menu } from "@mui/material";
import WorkbookItemCard from "../../shared/cards/WorkbookItemCard";
import { useBanner } from "../../context/BannerContext";
import { useUserSettings } from "../../context/UserSettingsContext";
import { useDatasources } from "../../context/DatasourceContext";
import { hasDataTableDatasource } from "../../hooks/useCanAddDataTables";
import { useReportActions } from "../../hooks/useReportActions";
import { useDataTableActions } from "../../hooks/useDataTableActions";
import { deleteDataWorksheets, toggleDataWorksheets, updateShowDataSheetsState } from "../../../excel/workbook";
import { DatasourceGoneError } from "../../../errors";
import {
  DATA_TABLES_DELETE_NOTICE,
  DATA_TABLES_LOADED,
  DATA_TABLES_REFRESH_ERROR,
  REPORT_REFRESH_PENDING,
  DATA_TABLES_REMOVED,
  DATA_TABLES_REMOVE_ERROR,
  SHOW_DATA_SHEETS_LABEL,
} from "../../constants";

const DataTablesCard = () => {
  const { settings, setHasDataSheets, updateSettings } = useUserSettings();
  const { ediphiDatasources } = useDatasources();
  const hasData = hasDataTableDatasource(ediphiDatasources);
  const { showBanner } = useBanner();
  const { withReportLogging } = useReportActions();
  const { loadDataTables } = useDataTableActions();

  const [progress, setProgress] = useState<string | null>(null);
  const [optionsAnchor, setOptionsAnchor] = useState<HTMLElement | null>(null);

  const handleRefresh = async () => {
    if (progress) return;
    setProgress("Refreshing data tables…");
    try {
      await withReportLogging(
        () => loadDataTables(true, { onChangesDetected: () => showBanner(REPORT_REFRESH_PENDING, "info") }),
        {
          context: "DataTablesCard_handleRefresh",
          message: DATA_TABLES_REFRESH_ERROR,
        }
      );
      showBanner(DATA_TABLES_LOADED, "success");
    } catch (error) {
      const message = error instanceof DatasourceGoneError ? error.message : "Failed to load data tables";
      showBanner(message, "error");
    } finally {
      setProgress(null);
    }
  };

  const handleRemove = async () => {
    try {
      // Each of these opens its own Excel.run and Office.js can't run them concurrently, so they
      // stay sequential.
      await deleteDataWorksheets();
      await updateShowDataSheetsState(false);
      await updateSettings({ showDataSheets: false });
      setHasDataSheets(false);
      showBanner(DATA_TABLES_REMOVED, "success");
    } catch {
      showBanner(DATA_TABLES_REMOVE_ERROR, "error");
    }
  };

  const handleShowDataSheetsChange = async (checked: boolean) => {
    await updateSettings({ showDataSheets: checked });
    await updateShowDataSheetsState(checked);
    await toggleDataWorksheets(checked);
  };

  return (
    <>
      <WorkbookItemCard
        header={{
          text: "Data Tables",
          name: "Data Tables",
          subtext: hasData ? "Ediphi Data" : "No data available",
          subtextIsStatus: !hasData,
        }}
        itemNoun="data tables"
        action={{ onClick: () => void handleRefresh(), isVisible: hasData }}
        edit={{ onClick: (event) => setOptionsAnchor(event.currentTarget) }}
        remove={{
          onClick: () => void handleRemove(),
          notice: DATA_TABLES_DELETE_NOTICE,
          noticeSeverity: "warning",
        }}
        loading={!!progress}
        loadingText={progress ?? undefined}
      />
      <Menu
        anchorEl={optionsAnchor}
        open={!!optionsAnchor}
        onClose={() => setOptionsAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box sx={{ px: 2, py: 1, display: "flex", flexDirection: "column", minWidth: "220px" }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={settings.showDataSheets}
                onChange={(event) => void handleShowDataSheetsChange(event.target.checked)}
                size="small"
              />
            }
            label={SHOW_DATA_SHEETS_LABEL}
          />
        </Box>
      </Menu>
    </>
  );
};

export default DataTablesCard;
